import type { AxiosRequestConfig } from 'axios'

import type { ApiResponse, ApisauceInstance } from '@mangovoice/apisauce'

import type {
  HttpTransport,
  TransportErrorResponse,
  TransportOkResponse,
  TransportRequestConfig,
  TransportResponse,
} from './types'

/**
 * Maps the framework-agnostic TransportRequestConfig to an AxiosRequestConfig.
 */
function toAxiosConfig(
  config?: TransportRequestConfig,
): AxiosRequestConfig | undefined {
  if (!config) return undefined

  const axiosConfig: AxiosRequestConfig = {}

  if (config.signal) axiosConfig.signal = config.signal
  if (config.headers) axiosConfig.headers = config.headers
  if (config.timeout !== undefined) axiosConfig.timeout = config.timeout
  if (config.onUploadProgress)
    axiosConfig.onUploadProgress = config.onUploadProgress
  if (config.onDownloadProgress)
    axiosConfig.onDownloadProgress = config.onDownloadProgress
  if (config.responseType) axiosConfig.responseType = config.responseType
  if (config.data !== undefined) axiosConfig.data = config.data

  return axiosConfig
}

/**
 * Normalizes an apisauce ApiResponse into the slimmer TransportResponse shape.
 */
function normalizeResponse<T>(
  response: ApiResponse<T, unknown>,
): TransportResponse<T> {
  if (response.ok) {
    return {
      ok: true,
      data: response.data as T,
      status: response.status ?? 200,
      headers: response.headers as Record<string, string> | undefined,
    } satisfies TransportOkResponse<T>
  }

  return {
    ok: false,
    data: response.data,
    status: response.status ?? 0,
    problem: response.problem,
  } satisfies TransportErrorResponse
}

/**
 * Creates an HttpTransport adapter that wraps an apisauce instance.
 * This replaces the repetitive wrapApiCall/wrapAxiosCall patterns
 * found across domain libraries.
 */
export function createTransportAdapter(
  instance: ApisauceInstance,
): HttpTransport {
  return {
    async get<T>(
      url: string,
      params?: Record<string, unknown>,
      config?: TransportRequestConfig,
    ): Promise<TransportResponse<T>> {
      const response = await instance.get<T>(url, params, toAxiosConfig(config))
      return normalizeResponse(response)
    },

    async post<T>(
      url: string,
      data?: unknown,
      config?: TransportRequestConfig,
    ): Promise<TransportResponse<T>> {
      const response = await instance.post<T>(url, data, toAxiosConfig(config))
      return normalizeResponse(response)
    },

    async put<T>(
      url: string,
      data?: unknown,
      config?: TransportRequestConfig,
    ): Promise<TransportResponse<T>> {
      const response = await instance.put<T>(url, data, toAxiosConfig(config))
      return normalizeResponse(response)
    },

    async patch<T>(
      url: string,
      data?: unknown,
      config?: TransportRequestConfig,
    ): Promise<TransportResponse<T>> {
      const response = await instance.patch<T>(url, data, toAxiosConfig(config))
      return normalizeResponse(response)
    },

    async delete<T>(
      url: string,
      params?: Record<string, unknown>,
      config?: TransportRequestConfig,
    ): Promise<TransportResponse<T>> {
      const response = await instance.delete<T>(
        url,
        params,
        toAxiosConfig(config),
      )
      return normalizeResponse(response)
    },
  }
}
